import { useMutation as useCartUpdateMutation } from "@faire/web-api/api/v2/carts/cartToken/put-hooks";
import { IUpdateCartRequestV2 } from "@faire/web-api/faire/carts/IUpdateCartRequestV2";
import { IUpdateCartResponseV2 } from "@faire/web-api/faire/carts/IUpdateCartResponseV2";
import cloneDeep from "lodash/cloneDeep";
import { useCallback, useMemo } from "react";

import { useInvalidateCart } from "@faire/retailer/features/Cart/useCart";
import { commonThrottle } from "@faire/retailer/lib/commonThrottle";
import { generateBrandCartForUpdates } from "@faire/retailer/lib/updateCartV2";
import { getCartToken } from "@faire/retailer/serialized-data/getCartToken";
import {
  ICartItemForUpdate,
  IUpdateItemsOptions,
  UpdateCartArg,
  UpdateCartItemsHandler,
} from "@faire/retailer/stores/domain/CartStore/consts";
import { triggerAddToCartEventV2 } from "@faire/retailer/stores/domain/CartStore/triggerAddToCartEvent";

import { useAddToCartTracking } from "../uplt/useAddToCartTracking";

import { useCartItems, useInvalidateCartItems } from "./useCartItems";
import { useInvalidateCartSummary } from "./useCartSummary";

export const useCartUpdate = () => {
  const { leanCartItemMap } = useCartItems();
  const { invalidateCartItemInventory, invalidateLeanBrandsMap } =
    useInvalidateCartItems();
  const { invalidateEntireCartSummary } = useInvalidateCartSummary();
  const { invalidateActiveCart } = useInvalidateCart();
  const { startTracking } = useAddToCartTracking();

  const { mutateAsync: updateCartMutation, ...mutationResult } =
    useCartUpdateMutation();

  const updateCartItems: UpdateCartItemsHandler = useCallback(
    async ({
      items,
      brand_carts,
      requested_ship_dates,
      allowEmptyItems,
      skipCartRefetch,
      upltParams,
    }: UpdateCartArg) => {
      if (!allowEmptyItems && !items?.length) {
        return;
      }

      const request: IUpdateCartRequestV2 = IUpdateCartRequestV2.build({
        brand_carts: generateBrandCartForUpdates(
          brand_carts ?? [],
          items,
          requested_ship_dates
        ),
      });

      if (upltParams.event === "add-to-cart") {
        startTracking({
          items: items.map(({ product_option_token, quantity }) => ({
            productOptionToken: product_option_token ?? "",
            quantity: quantity ?? 0,
          })),
          ...upltParams,
        });
      }
      // TODO: If experiment ships, consider moving the trackTiming in QuickAdd here and use regular mutate instead
      return updateCartMutation([getCartToken(), request], {
        onSuccess: (updateResponse: IUpdateCartResponseV2) => {
          triggerAddToCartEventV2(
            updateResponse,
            cloneDeep(leanCartItemMap ?? {}),
            upltParams
          );
          invalidateCartItemInventory();
          invalidateLeanBrandsMap();
          invalidateEntireCartSummary();
          if (!skipCartRefetch) {
            // If we add to cart from other pages, we still need to invalidate the cart page cart
            invalidateActiveCart();
          }
        },
      });
    },
    [
      invalidateActiveCart,
      invalidateCartItemInventory,
      invalidateEntireCartSummary,
      invalidateLeanBrandsMap,
      leanCartItemMap,
      startTracking,
      updateCartMutation,
    ]
  );

  const throttledUpdateCartItems: UpdateCartItemsHandler = useMemo(
    () => commonThrottle(updateCartItems),
    [updateCartItems]
  );

  // TODO: This is a temporary wrapper for updateCartItems to fit in UpdateCartHandler,
  // we should be able to delete this when we fully migrate to hooks
  const updateCartItemsWithUpdateHandler = async (
    items: ICartItemForUpdate[],
    options: IUpdateItemsOptions
  ) => {
    const { upltParams } = options;
    await throttledUpdateCartItems({
      items,
      upltParams,
    });
  };

  return {
    ...mutationResult,
    updateCartItems: throttledUpdateCartItems,
    updateCartItemsWithUpdateHandler,
  };
};
